import { formatDate, todayIst } from "@nexseed/shared";

/** A plant day is an Indian calendar day, whatever timezone the browser is set to. */
export const todayInIndia = (now = new Date()): string => todayIst(now);

/** The time in India as a "YYYY-MM-DDTHH:mm" stamp, the shape a datetime field holds (no DST). */
export const nowInIndia = (now = new Date()): string =>
  new Date(now.getTime() + 330 * 60_000).toISOString().slice(0, 16);

/** Good morning / afternoon / evening, by the plant's clock (India), not the browser's. */
export function greeting(now = new Date()): string {
  const h = Number(
    now.toLocaleString("en-US", {
      timeZone: "Asia/Kolkata",
      hour: "numeric",
      hour12: false,
    })
  );
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

/** "Friday, 18/09/2026", the day as the plant in India reads it. */
export function longToday(now = new Date()): string {
  const weekday = now.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
  });
  return `${weekday}, ${formatDate(todayInIndia(now))}`;
}

/** How a list is narrowed by date. No mode at all means every date. */
export const DATE_MODES = ["today", "on", "range"] as const;
export type DateMode = (typeof DATE_MODES)[number];

/**
 * The from/to a date filter sends to the API.
 *
 * "Today" is resolved here, at read time, rather than written into the link: a bookmarked "Today"
 * must still mean today tomorrow. A link carrying from/to with no mode predates the modes and reads
 * as the range it always was.
 */
export function resolveDateFilter(
  date: DateMode | undefined,
  from: string | undefined,
  to: string | undefined,
  today: string
): { from?: string | undefined; to?: string | undefined } {
  if (date === "today") return { from: today, to: today };
  if (date === "on") return from ? { from, to: from } : {};
  return { from, to };
}

/** How long since a moment, as the "On the road" boards say it: "5h 20m", "2d 3h". */
export function since(iso: string | null): string | null {
  if (!iso) return null;
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 48) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}
